import { useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPalette,
  faRedo,
  faDownload,
  faVectorSquare,
  faTrash,
  faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import Loading from "./Loading";

function Paintings({ predictions }) {
  const [paintings, setPaintings] = useState(null);
  const [svgPoints, setSvgPoints] = useState(null);
  const [instructions, setInstructions] = useState(null);
  const [visualizationUrl, setVisualizationUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [mode, setMode] = useState("binary");
  const [selectedModel, setSelectedModel] = useState("gan_v1_128");

  const fetchPaintings = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        "http://localhost:5000/generate-images",
        { predictions, model: selectedModel }
      );
      setPaintings(response.data.images);
      setSelectedImageIndex(null);
    } catch (error) {
      console.error("Error during analysis or image generation:", error);
      alert("Make sure the server is running!");
    } finally {
      setLoading(false);
    }
  };

  const processPainting = async () => {
    if (selectedImageIndex === null) return;
    setLoading(true);
    try {
      const response = await axios.post(
        "http://localhost:5000/process-painting",
        { image: paintings[selectedImageIndex], mode: mode }
      );
      setSvgPoints(response.data.points);
      setInstructions(response.data.instructions);
      setVisualizationUrl(response.data.visualization);
    } catch (error) {
      console.error("Error during SVG conversion:", error);
    } finally {
      setLoading(false);
    }
  };

  const downloadInstructions = () => {
    const blob = new Blob([instructions], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "instructions.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetAll = () => {
    setPaintings(null);
    setSvgPoints(null);
    setInstructions(null);
    setVisualizationUrl(null);
    setSelectedImageIndex(null);
  };

  const goBack = () => {
    setVisualizationUrl(null);
    setSvgPoints(null);
    setInstructions(null);
  };

  return (
    <div className="m-5 flex flex-col items-center justify-start">
      {!visualizationUrl && (
        <div className="flex flex-col w-full mb-4 space-y-2">
          <div className="flex space-x-2">
            <select
              className="flex-1 bg-white border border-gray-300 text-gray-700 py-3 px-4 pr-8 rounded-l-full leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
            >
              <option value="gan_v1_128">GAN Model - 1 (128 px)</option>
              <option value="gan_v2_128">GAN Model - 2 (128 px)</option>
              <option value="dpdm_64">DPDM Model (64px)</option>
            </select>
            <button
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-r-full disabled:bg-gray-400 disabled:cursor-not-allowed text-sm transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              onClick={fetchPaintings}
              disabled={!predictions || loading || paintings}
            >
              <FontAwesomeIcon icon={faPalette} className="mr-2" />
              Generate Paintings
            </button>
            <button
              className="flex-none bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-full text-sm transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
              onClick={fetchPaintings}
            >
              <FontAwesomeIcon icon={faRedo} />
            </button>
          </div>
        </div>
      )}
      {loading && <Loading />}
      {paintings && !loading && !visualizationUrl && (
        <>
          <div className="grid grid-cols-2 gap-4 w-full">
            {paintings.map((base64Image, index) => (
              <div
                key={index}
                className={`relative overflow-hidden rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer ${
                  selectedImageIndex === index ? "ring-4 ring-blue-500" : ""
                }`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <img
                  src={`data:image/png;base64,${base64Image}`}
                  alt={`Generated ${index}`}
                  className="w-full h-auto object-cover"
                />
              </div>
            ))}
          </div>
          <div className="flex items-center mt-4 space-x-4">
            <label className="inline-flex items-center cursor-pointer">
              <span
                className={`mr-2 text-md font-${
                  mode === "binary" ? "bold" : "semibold"
                } text-gray-600`}
              >
                Binary
              </span>
              <input
                type="checkbox"
                className="sr-only peer"
                checked={mode === "color"}
                onChange={() => setMode(mode === "binary" ? "color" : "binary")}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 relative">
                <div
                  className={`w-5 h-5 mt-0.5 ml-0.5 bg-white rounded-full shadow-md transform transition duration-300 ${
                    mode === "color" ? "translate-x-5 bg-green-500" : ""
                  }`}
                ></div>
              </div>
              <span
                className={`ml-2 text-md font-${
                  mode === "color" ? "bold" : "semibold"
                } text-gray-600`}
              >
                Color
              </span>
            </label>
          </div>
          <button
            className={`mt-4 bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full text-sm transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 ${
              selectedImageIndex === null ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={processPainting}
            disabled={selectedImageIndex === null}
          >
            <FontAwesomeIcon icon={faVectorSquare} className="mr-2" />
            Generate SVG
          </button>
        </>
      )}
      {visualizationUrl && !loading && (
        <div className="w-full">
          <img
            src={visualizationUrl}
            alt="2D Points Visualization"
            className="w-full object-contain rounded-lg shadow-md mb-4"
          />
          <div className="flex flex-col items-center w-full">
            <div className="w-3/4 mb-4 flex justify-center">
              <div
                id="svgPoints"
                className="overflow-hidden rounded-lg shadow-md"
                dangerouslySetInnerHTML={{ __html: svgPoints }}
              />
            </div>
            <div className="flex items-center space-x-4">
              <button
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold p-4 rounded-full text-lg transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                onClick={goBack}
                title="Go Back"
              >
                <FontAwesomeIcon icon={faArrowLeft} size="2x" />
              </button>
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white font-bold p-4 rounded-full text-lg transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50"
                onClick={downloadInstructions}
                title="Download Instructions"
              >
                <FontAwesomeIcon icon={faDownload} size="2x" />
              </button>
              <button
                className="bg-red-500 hover:bg-red-600 text-white font-bold p-4 rounded-full text-lg transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                onClick={resetAll}
                title="Reset All"
              >
                <FontAwesomeIcon icon={faTrash} size="2x" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              <strong>Back</strong>, <strong>Download</strong> 2D points, or{" "}
              <strong>Reset</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default Paintings;
